import type { ReactNode } from "react";
import { RadioCard, RadioGroup } from "../../primitives";

export type RadioCardOption<TValue> = {
	value: TValue;
	/** Primary text — maps to `RadioCard.Title`. */
	label: ReactNode;
	/** Maps to `RadioCard.Description`. */
	description?: ReactNode;
	/** Maps to `RadioCard.Footer`. */
	footer?: { leadingIcon?: ReactNode; text: ReactNode };
	disabled?: boolean;
};

export type RadioCardSelectProps<TValue extends string | number | boolean> = {
	value: TValue | null;
	onChange: (value: TValue) => void;
	options: RadioCardOption<TValue>[];
	direction?: "row" | "column";
	gap?: "sm" | "md" | "lg";
	disabled?: boolean;
	className?: string;
	testId?: string;
	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;
};

export const RadioCardSelect = <TValue extends string | number | boolean = string>({
	value,
	onChange,
	options,
	direction = "column",
	gap = "md",
	disabled,
	className,
	testId,
	trackingId,
}: RadioCardSelectProps<TValue>) => {
	const optionTestId = (optValue: TValue) => (testId ? `${testId}-${String(optValue)}` : undefined);

	return (
		<RadioGroup<TValue | null>
			value={value}
			onChange={(next) => {
				if (next !== null) onChange(next);
			}}
			direction={direction}
			gap={gap}
			disabled={disabled}
			className={className}
			data-testid={testId}
			trackingId={trackingId}
		>
			{options.map((option) => (
				<RadioCard
					key={String(option.value)}
					value={option.value}
					disabled={option.disabled}
					data-testid={optionTestId(option.value)}
				>
					<RadioCard.Title>{option.label}</RadioCard.Title>
					{option.description !== undefined && (
						<RadioCard.Description>{option.description}</RadioCard.Description>
					)}
					{option.footer !== undefined && (
						<RadioCard.Footer leadingIcon={option.footer.leadingIcon}>
							{option.footer.text}
						</RadioCard.Footer>
					)}
				</RadioCard>
			))}
		</RadioGroup>
	);
};
